import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../contexts/TranslationContext";
import { PencilIcon, TrashIcon, PlayIcon } from "@heroicons/react/24/outline";
import { Form } from "../../../types";

interface FormHeaderProps {
  form: Form;
  onToggleStatus: () => void;
  onTest: () => void;
  onDelete: () => void;
  isToggling: boolean;
  isDeleting: boolean;
}

const FormHeader: React.FC<FormHeaderProps> = ({
  form,
  onToggleStatus,
  onTest,
  onDelete,
  isToggling,
  isDeleting,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="md:flex md:items-center md:justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {form.name}
          </h2>
          <span
            className={`ml-3 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              form.is_active
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {form.is_active
              ? t("forms.detail.active")
              : t("forms.detail.inactive")}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          {t("forms.detail.created")}{" "}
          {form.created_at
            ? new Date(form.created_at).toLocaleDateString()
            : "N/A"}
        </p>
      </div>
      <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
        <button
          onClick={onToggleStatus}
          disabled={isToggling}
          className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
            form.is_active
              ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
              : "bg-green-600 hover:bg-green-700 focus:ring-green-500"
          }`}
        >
          {form.is_active
            ? t("forms.detail.deactivate")
            : t("forms.detail.activate")}
        </button>
        <button
          onClick={onTest}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlayIcon className="w-4 h-4 mr-2" />
          {t("forms.detail.testForm")}
        </button>
        <button
          onClick={() => navigate(`/forms/${form.id}/edit`)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PencilIcon className="w-4 h-4 mr-2" />
          {t("forms.detail.edit")}
        </button>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
        >
          <TrashIcon className="w-4 h-4 mr-2" />
          Delete
        </button>
      </div>
    </div>
  );
};

export default FormHeader;

